/**
 * RotaBuilder.jsx
 * 
 * Weekly rota builder with drag-and-drop shift assignment.
 * Managers can assign shifts to employees for each day of the week.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  BeachAccess as HolidayIcon,
  Schedule as ShiftIcon
} from '@mui/icons-material';

// Default shift templates
const DEFAULT_TEMPLATES = [
  { id: 'early', name: 'Early', start_time: '09:00', end_time: '17:00', break_duration: 30, color: '#4CAF50' },
  { id: 'late', name: 'Late', start_time: '12:00', end_time: '20:00', break_duration: 30, color: '#2196F3' },
  { id: 'full', name: 'Full Day', start_time: '09:00', end_time: '20:00', break_duration: 60, color: '#9C27B0' },
  { id: 'morning', name: 'Morning', start_time: '09:00', end_time: '13:00', break_duration: 0, color: '#FF9800' },
  { id: 'afternoon', name: 'Afternoon', start_time: '14:00', end_time: '20:00', break_duration: 0, color: '#E91E63' },
  { id: 'off', name: 'Day Off', start_time: null, end_time: null, break_duration: 0, color: '#9E9E9E' },
];

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const RotaBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [shifts, setShifts] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialog states
  const [shiftDialog, setShiftDialog] = useState({ open: false, employeeId: null, date: null, existing: null });
  const [publishDialog, setPublishDialog] = useState(false);
  const [copyDialog, setCopyDialog] = useState(false);
  
  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) return weekParam;
    
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    return monday.toISOString().split('T')[0];
  });

  useEffect(() => {
    loadData();
  }, [currentWeekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [weekResponse, templatesResponse] = await Promise.all([
        api.get(`/rota/week?week_start=${currentWeekStart}`),
        api.get('/rota/templates')
      ]);
      
      setWeekData(weekResponse.data);
      setShifts(weekResponse.data.shifts || {});
      
      if (templatesResponse.data.templates?.length > 0) {
        setTemplates(templatesResponse.data.templates);
      }
    } catch (err) {
      console.error('Failed to load rota data:', err);
      setSnackbar({ open: true, message: 'Failed to load rota data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    if (unsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
    setUnsavedChanges(false);
  };

  const getDateForDay = (dayIndex) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayIndex);
    return date.toISOString().split('T')[0];
  };

  const formatWeekRange = () => {
    const start = new Date(currentWeekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    
    const options = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)} ${start.getFullYear()}`;
  };

  const isEmployeeOnHoliday = (employeeId, dateStr) => {
    const employee = weekData?.employees?.find(e => e.id === employeeId);
    return employee?.holidays?.includes(dateStr);
  };

  const getShiftKey = (employeeId, dateStr) => `${employeeId}_${dateStr}`;

  const handleCellClick = (employeeId, dayIndex) => {
    const dateStr = getDateForDay(dayIndex);
    
    if (isEmployeeOnHoliday(employeeId, dateStr)) {
      setSnackbar({ open: true, message: 'Employee is on approved holiday this day', severity: 'warning' });
      return;
    }
    
    const key = getShiftKey(employeeId, dateStr);
    const existing = shifts[key] || null;
    
    setShiftDialog({
      open: true,
      employeeId,
      date: dateStr,
      existing
    });
  };

  const handleShiftSave = async (template) => {
    const { employeeId, date } = shiftDialog;
    
    try {
      setSaving(true);
      
      const payload = {
        employee_id: employeeId,
        date: date,
        start_time: template.start_time,
        end_time: template.end_time,
        break_duration: template.break_duration || 0,
        template_name: template.name,
        color: template.color
      };
      
      const response = await api.post('/rota/shift', payload);
      
      if (response.data.success) {
        const key = getShiftKey(employeeId, date);
        setShifts(prev => ({
          ...prev,
          [key]: response.data.shift
        }));
        setSnackbar({ open: true, message: 'Shift saved', severity: 'success' });
      }
    } catch (err) {
      console.error('Failed to save shift:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save shift', severity: 'error' });
    } finally {
      setSaving(false);
      setShiftDialog({ open: false, employeeId: null, date: null, existing: null });
    }
  };

  const handleShiftDelete = async () => {
    const { existing } = shiftDialog;
    
    if (!existing?.id) {
      setShiftDialog({ open: false, employeeId: null, date: null, existing: null });
      return;
    }
    
    try {
      setSaving(true);
      await api.delete(`/rota/shift/${existing.id}`);
      
      const key = getShiftKey(shiftDialog.employeeId, shiftDialog.date);
      setShifts(prev => {
        const newShifts = { ...prev };
        delete newShifts[key];
        return newShifts;
      });
      
      setSnackbar({ open: true, message: 'Shift removed', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete shift:', err);
      setSnackbar({ open: true, message: 'Failed to remove shift', severity: 'error' });
    } finally {
      setSaving(false);
      setShiftDialog({ open: false, employeeId: null, date: null, existing: null });
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);
      const response = await api.post('/rota/publish', { week_start: currentWeekStart });
      
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Rota published! Your team can now see their shifts.', severity: 'success' });
        setPublishDialog(false);
        loadData(); // Refresh to show published status
      }
    } catch (err) {
      console.error('Failed to publish rota:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to publish rota', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyWeek = async () => {
    try {
      setSaving(true);
      
      // Get previous week's Monday
      const prevWeek = new Date(currentWeekStart);
      prevWeek.setDate(prevWeek.getDate() - 7);
      const sourceWeek = prevWeek.toISOString().split('T')[0];
      
      const response = await api.post('/rota/copy-week', {
        source_week: sourceWeek,
        target_week: currentWeekStart
      });
      
      if (response.data.success) {
        setSnackbar({ 
          open: true, 
          message: `Copied ${response.data.copied} shifts (${response.data.skipped} skipped due to holidays or existing shifts)`, 
          severity: 'success' 
        });
        setCopyDialog(false);
        loadData();
      }
    } catch (err) {
      console.error('Failed to copy week:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to copy rota', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getCoverageForDay = (dayIndex) => {
    const dateStr = getDateForDay(dayIndex);
    return weekData?.coverage?.[dateStr] || { working: 0, on_holiday: 0, unassigned: 0 };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/rota')}>
                <BackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Rota Builder
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => navigateWeek(-1)}>
                    <BackIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {formatWeekRange()}
                  </Typography>
                  <IconButton size="small" onClick={() => navigateWeek(1)}>
                    <ForwardIcon fontSize="small" />
                  </IconButton>
                  {weekData?.is_published && (
                    <Chip label="Published" size="small" color="success" icon={<CheckIcon />} />
                  )}
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => setCopyDialog(true)}
              >
                Copy Previous Week
              </Button>
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => setPublishDialog(true)}
                sx={{ 
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  '&:hover': { opacity: 0.9 }
                }}
              >
                Publish Rota
              </Button>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Shift Templates Legend */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Shift Templates (click a cell to assign)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {templates.map((template) => (
              <Chip
                key={template.id}
                label={`${template.name}${template.start_time ? ` (${template.start_time}-${template.end_time})` : ''}`}
                sx={{
                  bgcolor: template.color + '20',
                  color: template.color,
                  borderColor: template.color,
                  fontWeight: 600
                }}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>

        {/* Rota Grid */}
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 900 }}>
              {/* Header Row */}
              <Box sx={{ display: 'flex', borderBottom: '2px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Box sx={{ width: 180, p: 2, borderRight: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Employee
                  </Typography>
                </Box>
                {DAY_NAMES.map((day, index) => {
                  const dateStr = getDateForDay(index);
                  const dateObj = new Date(dateStr);
                  const isToday = dateStr === new Date().toISOString().split('T')[0];
                  const coverage = getCoverageForDay(index);
                  
                  return (
                    <Box
                      key={day}
                      sx={{
                        flex: 1,
                        p: 2,
                        textAlign: 'center',
                        borderRight: '1px solid #e2e8f0',
                        bgcolor: isToday ? '#667eea10' : 'inherit'
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold" color={isToday ? '#667eea' : 'inherit'}>
                        {SHORT_DAYS[index]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dateObj.getDate()}/{dateObj.getMonth() + 1}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={`${coverage.working} working`}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#4CAF5020', color: '#4CAF50' }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {/* Employee Rows */}
              {weekData?.employees?.map((employee) => (
                <Box
                  key={employee.id}
                  sx={{
                    display: 'flex',
                    borderBottom: '1px solid #e2e8f0',
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  {/* Employee Name */}
                  <Box sx={{ width: 180, p: 2, borderRight: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" fontWeight="600">
                      {employee.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employee.job_title || 'Staff'}
                    </Typography>
                  </Box>

                  {/* Day Cells */}
                  {DAY_NAMES.map((day, dayIndex) => {
                    const dateStr = getDateForDay(dayIndex);
                    const isHoliday = isEmployeeOnHoliday(employee.id, dateStr);
                    const key = getShiftKey(employee.id, dateStr);
                    const shift = shifts[key];
                    const isToday = dateStr === new Date().toISOString().split('T')[0];

                    return (
                      <Box
                        key={day}
                        onClick={() => handleCellClick(employee.id, dayIndex)}
                        sx={{
                          flex: 1,
                          p: 1,
                          minHeight: 70,
                          borderRight: '1px solid #e2e8f0',
                          cursor: isHoliday ? 'not-allowed' : 'pointer',
                          bgcolor: isHoliday 
                            ? '#FF980015' 
                            : isToday 
                              ? '#667eea08' 
                              : 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: isHoliday ? '#FF980015' : '#667eea10'
                          }
                        }}
                      >
                        {isHoliday ? (
                          <Box sx={{ textAlign: 'center', py: 1 }}>
                            <HolidayIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                            <Typography variant="caption" display="block" color="#FF9800" fontWeight="600">
                              Holiday
                            </Typography>
                          </Box>
                        ) : shift?.start_time ? (
                          <Box
                            sx={{
                              bgcolor: shift.color + '20',
                              border: `1px solid ${shift.color}`,
                              borderRadius: 1,
                              p: 1,
                              height: '100%'
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold" color={shift.color}>
                              {shift.template_name}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {shift.start_time} - {shift.end_time}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2, opacity: 0.3 }}>
                            <AddIcon sx={{ fontSize: 20 }} />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Coverage Summary */}
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Daily Coverage Summary
          </Typography>
          <Grid container spacing={2}>
            {DAY_NAMES.map((day, index) => {
              const coverage = getCoverageForDay(index);
              const hasWarning = coverage.unassigned > Math.floor(weekData?.total_employees / 3);
              
              return (
                <Grid item xs={12} sm={6} md={12/7} key={day}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: hasWarning ? '1px solid #FF9800' : '1px solid #e2e8f0',
                      bgcolor: hasWarning ? '#FF980010' : 'white'
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      {SHORT_DAYS[index]}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">Working:</Typography>
                      <Typography variant="caption" fontWeight="bold" color="#4CAF50">
                        {coverage.working}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Holiday:</Typography>
                      <Typography variant="caption" fontWeight="bold" color="#FF9800">
                        {coverage.on_holiday}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Unassigned:</Typography>
                      <Typography variant="caption" fontWeight="bold" color={hasWarning ? '#FF9800' : '#9E9E9E'}>
                        {coverage.unassigned}
                      </Typography>
                    </Box>
                    {hasWarning && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <WarningIcon sx={{ fontSize: 14, color: '#FF9800' }} />
                        <Typography variant="caption" color="#FF9800">
                          Low coverage
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Container>

      {/* Shift Assignment Dialog */}
      <Dialog open={shiftDialog.open} onClose={() => setShiftDialog({ open: false, employeeId: null, date: null, existing: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {shiftDialog.existing ? 'Edit Shift' : 'Assign Shift'}
            </Typography>
            <IconButton onClick={() => setShiftDialog({ open: false, employeeId: null, date: null, existing: null })}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {weekData?.employees?.find(e => e.id === shiftDialog.employeeId)?.name} - {shiftDialog.date && new Date(shiftDialog.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            Select a shift template:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {templates.map((template) => (
              <Button
                key={template.id}
                variant={shiftDialog.existing?.template_name === template.name ? 'contained' : 'outlined'}
                onClick={() => handleShiftSave(template)}
                disabled={saving}
                sx={{
                  justifyContent: 'flex-start',
                  borderColor: template.color,
                  color: template.color,
                  bgcolor: shiftDialog.existing?.template_name === template.name ? template.color : 'transparent',
                  '&:hover': {
                    bgcolor: template.color + '20',
                    borderColor: template.color
                  }
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {template.name}
                  </Typography>
                  {template.start_time && (
                    <Typography variant="caption">
                      {template.start_time} - {template.end_time}
                      {template.break_duration > 0 && ` (${template.break_duration}m break)`}
                    </Typography>
                  )}
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        {shiftDialog.existing && (
          <DialogActions>
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleShiftDelete}
              disabled={saving}
            >
              Remove Shift
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialog} onClose={() => setPublishDialog(false)}>
        <DialogTitle>Publish Rota</DialogTitle>
        <DialogContent>
          <Typography>
            Publishing this rota will make it visible to all employees.
            They will be able to see their scheduled shifts for the week of {formatWeekRange()}.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Make sure you've assigned all shifts before publishing.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handlePublish}
            disabled={saving}
          >
            {saving ? 'Publishing...' : 'Publish Rota'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Week Confirmation Dialog */}
      <Dialog open={copyDialog} onClose={() => setCopyDialog(false)}>
        <DialogTitle>Copy Previous Week</DialogTitle>
        <DialogContent>
          <Typography>
            This will copy all shifts from last week to the current week ({formatWeekRange()}).
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Shifts will not be copied for employees who are on holiday or already have shifts assigned.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCopyWeek}
            disabled={saving}
          >
            {saving ? 'Copying...' : 'Copy Shifts'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RotaBuilder;